import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    whatsapp: '',
    empresa: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d62mpsqbdoed7v0v8es0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          email: '',
          whatsapp: '',
          empresa: '',
          mensagem: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatWhatsApp = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleWhatsAppChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatWhatsApp(e.target.value);
    setFormData({ ...formData, whatsapp: formatted });
  };

  return (
    <section id="contact" className="py-24 bg-[#FAFBFC] relative overflow-hidden">
      {/* Glow verde sutil no canto inferior direito */}
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#72BF3B]/[0.04] rounded-full blur-3xl translate-y-1/3 translate-x-1/3 pointer-events-none"></div>
      
      {/* Linha separadora sutil no topo */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#72BF3B]/30 to-transparent"></div>
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-gray-500 text-sm font-mono mb-4">/contato</p>
            <h2 className="text-5xl font-bold text-[#162840] mb-4 leading-tight">
              Vamos conversar
            </h2>
            <p className="text-lg text-[#162840]/70 mb-8">
              <strong>BitFlow Solutions</strong> — Tecnologia no fluxo certo
            </p>
            
            <div className="space-y-6 mb-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#72BF3B]/10 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-[#72BF3B]"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#162840] mb-1">Telefone</p>
                  <a href="tel:+5548996804330" className="text-gray-600 hover:text-[#72BF3B] transition-colors cursor-pointer">(48) 99680-4330</a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#72BF3B]/10 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-[#72BF3B]"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#162840] mb-1">Email</p>
                  <a href="mailto:contato@bitflowsolutions.com.br" className="text-gray-600 hover:text-[#72BF3B] transition-colors cursor-pointer">contato@bitflowsolutions.com.br</a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#72BF3B]/10 rounded-lg flex-shrink-0">
                  <i className="ri-whatsapp-line text-2xl text-[#72BF3B]"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#162840] mb-1">WhatsApp</p>
                  <a href="https://wa.me/5548996804330" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#72BF3B] transition-colors cursor-pointer">(48) 99680-4330</a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#72BF3B]/10 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-2xl text-[#72BF3B]"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#162840] mb-1">Atendimento</p>
                  <p className="text-gray-600">Segunda a Sexta: 9h às 18h</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100/50 p-8 lg:p-10">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              {submitStatus === 'success' && (
                <div className="bg-[#72BF3B]/10 border border-[#72BF3B]/20 text-[#5a9c2e] px-4 py-3 rounded-lg mb-4">
                  <p className="font-medium">Mensagem enviada com sucesso!</p>
                  <p className="text-sm">Entraremos em contato em breve.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  <p className="font-medium">Erro ao enviar mensagem.</p>
                  <p className="text-sm">Por favor, tente novamente.</p>
                </div>
              )}

              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                  Nome completo *
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#72BF3B] focus:border-[#72BF3B] outline-none transition-all"
                  placeholder="Seu nome"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#72BF3B] focus:border-[#72BF3B] outline-none transition-all"
                  placeholder="seu@email.com"
                />
              </div>

              <div>
                <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp *
                </label>
                <input
                  type="tel"
                  id="whatsapp"
                  name="whatsapp"
                  required
                  value={formData.whatsapp}
                  onChange={handleWhatsAppChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#72BF3B] focus:border-[#72BF3B] outline-none transition-all"
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                />
              </div>

              <div>
                <label htmlFor="empresa" className="block text-sm font-medium text-gray-700 mb-2">
                  Empresa *
                </label>
                <input
                  type="text"
                  id="empresa"
                  name="empresa"
                  required
                  value={formData.empresa}
                  onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#72BF3B] focus:border-[#72BF3B] outline-none transition-all"
                  placeholder="Nome da sua empresa"
                />
              </div>

              <div>
                <label htmlFor="mensagem" className="block text-sm font-medium text-gray-700 mb-2">
                  Mensagem
                </label>
                <textarea
                  id="mensagem"
                  name="mensagem"
                  rows={4}
                  maxLength={500}
                  value={formData.mensagem}
                  onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#72BF3B] focus:border-[#72BF3B] outline-none transition-all resize-none"
                  placeholder="Conte-nos sobre seu projeto ou necessidade..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.mensagem.length}/500 caracteres</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#72BF3B] text-white py-4 rounded-xl text-base font-semibold hover:bg-[#99D93B] transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 cursor-pointer whitespace-nowrap"
              >
                <span>{isSubmitting ? 'Enviando...' : 'Solicitar Contato'}</span>
                <i className="ri-send-plane-fill"></i>
              </button>

              <p className="text-xs text-gray-500 text-center">
                Ao enviar, você concorda com nossa <a href="#" className="underline hover:text-[#72BF3B]">política de privacidade</a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
